import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TenantService } from "@/services/tenantService";
import { FileUploadService } from "@/services/fileUploadService";
import { SupabaseImageService } from "@/services/supabaseImageService";
import type {
  TenantDbConfig,
  TableMigrationResult,
} from "@/services/tenantService";
import type { Tenant, TenantSettings, Plan } from "@/types/tenant";
import toast from "react-hot-toast";
import {
  TbToggleLeft,
  TbToggleRight,
  TbDatabase,
  TbCheck,
  TbX,
  TbLoader,
  TbArrowRight,
  TbArrowLeft,
  TbUpload,
  TbTrash,
} from "react-icons/tb";

type Section = "branding" | "owner" | "domain" | "features" | "database";

const FEATURE_FLAGS = [
  { key: "feature_arcgis", label: "ArcGIS Integration" },
  { key: "feature_superset", label: "Superset Analytics" },
  { key: "feature_realtime", label: "Real-time Updates" },
  { key: "feature_bulk_assignments", label: "Bulk Assignments" },
  { key: "feature_reporting", label: "Reporting" },
  { key: "feature_asset_management", label: "Asset Management" },
];

export const PlatformTenantConfig: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<Section>("branding");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [formData, setFormData] = useState<Partial<TenantSettings>>({
    site_title: "",
    logo_url: "",
    favicon_url: "",
    primary_color: "#374795",
    accent_color: "#2563eb",
    owner_name: "",
    owner_email: "",
    support_email: "",
    owner_phone: "",
    owner_address: "",
    subdomain: "",
    custom_domain: "",
    website_url: "",
    redirect_url: "",
    feature_superset: false,
    feature_arcgis: true,
    feature_realtime: true,
    feature_bulk_assignments: true,
    feature_reporting: true,
    feature_asset_management: true,
    plan_id: null,
    plan_tier: "free",
    max_users: 10,
    max_tasks: 1000,
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState<string | null>(
    null
  );
  const faviconInputRef = useRef<HTMLInputElement>(null);

  // Helper to resolve a storage path or full URL to a signed URL
  const resolveStorageUrl = (
    raw: string,
    setter: (url: string | null) => void
  ) => {
    if (!raw) {
      setter(null);
      return;
    }
    if (raw.startsWith("blob:")) {
      setter(raw);
      return;
    }
    const path = raw.includes("/storage/v1/object/public/task-files/")
      ? raw.split("/storage/v1/object/public/task-files/")[1]
      : raw;
    SupabaseImageService.getSignedUrl(path).then((url) => setter(url));
  };

  // Resolve stored logo/favicon paths to signed URLs for display
  useEffect(() => {
    resolveStorageUrl((formData.logo_url as string) || "", setLogoPreviewUrl);
  }, [formData.logo_url]);

  useEffect(() => {
    resolveStorageUrl(
      (formData.favicon_url as string) || "",
      setFaviconPreviewUrl
    );
  }, [formData.favicon_url]);

  const handleImageUpload = async (
    file: File,
    folder: string,
    field: string,
    setPreview: (url: string | null) => void,
    setUploading: (v: boolean) => void
  ) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const result = await FileUploadService.uploadFile(
        file,
        `tenants/${tenantId}/${folder}`
      );
      setFormData((prev) => ({ ...prev, [field]: result.path }));
      toast.success(
        `${field === "logo_url" ? "Logo" : "Favicon"} uploaded`
      );
    } catch (error) {
      setPreview(null);
      toast.error(
        error instanceof Error ? error.message : "Failed to upload"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (
    field: string,
    setPreview: (url: string | null) => void,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: "" }));
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const [subdomainStatus, setSubdomainStatus] = useState<{
    checking: boolean;
    available: boolean | null;
    reason?: string;
  }>({ checking: false, available: null });
  const [originalSubdomain, setOriginalSubdomain] = useState("");
  const subdomainDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounced subdomain availability check
  useEffect(() => {
    const subdomain = (formData.subdomain as string) || "";

    // Skip check if unchanged from original or too short
    if (!subdomain || subdomain.length < 3 || subdomain === originalSubdomain) {
      setSubdomainStatus({ checking: false, available: null });
      return;
    }

    setSubdomainStatus({ checking: true, available: null });

    if (subdomainDebounceRef.current)
      clearTimeout(subdomainDebounceRef.current);
    subdomainDebounceRef.current = setTimeout(async () => {
      const result = await TenantService.checkSlugAvailability(
        subdomain,
        tenantId
      );
      setSubdomainStatus({
        checking: false,
        available: result.available,
        reason: result.reason,
      });
    }, 400);

    return () => {
      if (subdomainDebounceRef.current)
        clearTimeout(subdomainDebounceRef.current);
    };
  }, [formData.subdomain, originalSubdomain, tenantId]);

  useEffect(() => {
    if (!tenantId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [tenants, settings, plansData] = await Promise.all([
          TenantService.listAllTenants(),
          TenantService.getTenantSettingsAsAdmin(tenantId),
          TenantService.listPlans(),
        ]);
        setPlans(plansData);
        const found = tenants.find((t: Tenant) => t.id === tenantId);
        setTenant(found || null);

        if (settings) {
          setFormData({
            site_title: settings.site_title || "",
            logo_url: settings.logo_url || "",
            favicon_url: settings.favicon_url || "",
            primary_color: settings.primary_color || "#374795",
            accent_color: settings.accent_color || "#2563eb",
            owner_name: settings.owner_name || "",
            owner_email: settings.owner_email || "",
            support_email: settings.support_email || "",
            owner_phone: settings.owner_phone || "",
            owner_address: settings.owner_address || "",
            subdomain: settings.subdomain || "",
            custom_domain: settings.custom_domain || "",
            website_url: settings.website_url || "",
            redirect_url: settings.redirect_url || "",
            feature_superset: settings.feature_superset ?? false,
            feature_arcgis: settings.feature_arcgis ?? true,
            feature_realtime: settings.feature_realtime ?? true,
            feature_bulk_assignments: settings.feature_bulk_assignments ?? true,
            feature_reporting: settings.feature_reporting ?? true,
            feature_asset_management: settings.feature_asset_management ?? true,
            plan_id: settings.plan_id || null,
            plan_tier: settings.plan_tier || "free",
            max_users: settings.max_users || 10,
            max_tasks: settings.max_tasks || 1000,
          });
          setOriginalSubdomain(settings.subdomain || "");
        }
      } catch {
        toast.error("Failed to load tenant data");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [tenantId]);

  const handleChange = (
    field: string,
    value: string | number | boolean | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      await TenantService.updateTenantSettingsAsAdmin(tenantId, formData);
      toast.success("Settings saved successfully");
      navigate("/platform/tenants");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const sections: { key: Section; label: string }[] = [
    { key: "branding", label: "Branding" },
    { key: "owner", label: "Owner Details" },
    { key: "domain", label: "Domain & URLs" },
    { key: "features", label: "Features & Limits" },
    { key: "database", label: "Database" },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-[#828282] text-sm mb-4">Tenant not found.</p>
        <button
          onClick={() => navigate("/platform/tenants")}
          className="text-sm font-medium text-[#374795] hover:text-[#1c398e] cursor-pointer"
        >
          Back to tenants
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <h2 className="text-lg font-semibold text-[#444]">
        Configure: {tenant.name}
      </h2>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-5">
        {/* Section tabs */}
        <div className="flex gap-2">
          {sections.map((section) => (
            <button
              key={section.key}
              onClick={() => setActiveSection(section.key)}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                activeSection === section.key
                  ? "bg-[#374795] text-white"
                  : "bg-gray-100 text-[#444] hover:bg-gray-200"
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Branding */}
        {activeSection === "branding" && (
          <div className="space-y-5">
            <InputField
              label="Site Title"
              value={(formData.site_title as string) || ""}
              onChange={(v) => handleChange("site_title", v)}
              placeholder="Your organization name"
            />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Logo
              </label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file)
                    handleImageUpload(
                      file,
                      "logos",
                      "logo_url",
                      setLogoPreviewUrl,
                      setLogoUploading
                    );
                }}
              />
              {formData.logo_url ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {logoPreviewUrl ? (
                    <img
                      src={logoPreviewUrl}
                      alt="Logo preview"
                      className="h-10 object-contain"
                    />
                  ) : (
                    <div className="h-10 w-20 bg-gray-200 rounded animate-pulse" />
                  )}
                  <div className="flex gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      className="cursor-pointer px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    >
                      {logoUploading ? "Uploading..." : "Change"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveImage(
                          "logo_url",
                          setLogoPreviewUrl,
                          logoInputRef
                        )
                      }
                      className="cursor-pointer p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove logo"
                    >
                      <TbTrash size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  className="cursor-pointer w-full flex items-center justify-center gap-2 px-3 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#374795] hover:text-[#374795] transition-colors disabled:opacity-50"
                >
                  <TbUpload size={18} />
                  {logoUploading ? "Uploading..." : "Upload Logo"}
                </button>
              )}
              <p className="mt-1 text-xs text-gray-400">
                JPG, PNG or GIF. Max 10MB.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Favicon
              </label>
              <input
                ref={faviconInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/x-icon,image/vnd.microsoft.icon"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file)
                    handleImageUpload(
                      file,
                      "favicons",
                      "favicon_url",
                      setFaviconPreviewUrl,
                      setFaviconUploading
                    );
                }}
              />
              {formData.favicon_url ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {faviconPreviewUrl ? (
                    <img
                      src={faviconPreviewUrl}
                      alt="Favicon preview"
                      className="h-8 w-8 object-contain"
                    />
                  ) : (
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                  )}
                  <div className="flex gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={() => faviconInputRef.current?.click()}
                      disabled={faviconUploading}
                      className="cursor-pointer px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    >
                      {faviconUploading ? "Uploading..." : "Change"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveImage(
                          "favicon_url",
                          setFaviconPreviewUrl,
                          faviconInputRef
                        )
                      }
                      className="cursor-pointer p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove favicon"
                    >
                      <TbTrash size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => faviconInputRef.current?.click()}
                  disabled={faviconUploading}
                  className="cursor-pointer w-full flex items-center justify-center gap-2 px-3 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#374795] hover:text-[#374795] transition-colors disabled:opacity-50"
                >
                  <TbUpload size={18} />
                  {faviconUploading ? "Uploading..." : "Upload Favicon"}
                </button>
              )}
              <p className="mt-1 text-xs text-gray-400">
                ICO, PNG, JPG or GIF. Max 10MB.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label="Primary Color"
                value={(formData.primary_color as string) || "#374795"}
                onChange={(v) => handleChange("primary_color", v)}
              />
              <ColorField
                label="Accent Color"
                value={(formData.accent_color as string) || "#2563eb"}
                onChange={(v) => handleChange("accent_color", v)}
              />
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-[#828282] mb-3">Preview</p>
              <div className="flex items-center gap-4">
                <div
                  className="w-24 h-10 rounded-lg"
                  style={{
                    backgroundColor:
                      (formData.primary_color as string) || "#374795",
                  }}
                />
                <span className="text-sm text-[#828282]">Primary</span>
                <div
                  className="w-24 h-10 rounded-lg"
                  style={{
                    backgroundColor:
                      (formData.accent_color as string) || "#2563eb",
                  }}
                />
                <span className="text-sm text-[#828282]">Accent</span>
              </div>
            </div>
          </div>
        )}

        {/* Owner Details */}
        {activeSection === "owner" && (
          <div className="space-y-5">
            <InputField
              label="Organization / Owner Name"
              value={(formData.owner_name as string) || ""}
              onChange={(v) => handleChange("owner_name", v)}
              placeholder="Acme Corporation"
            />
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Owner Email"
                value={(formData.owner_email as string) || ""}
                onChange={(v) => handleChange("owner_email", v)}
                type="email"
                placeholder="admin@acme.com"
              />
              <InputField
                label="Support Email"
                value={(formData.support_email as string) || ""}
                onChange={(v) => handleChange("support_email", v)}
                type="email"
                placeholder="support@acme.com"
              />
            </div>
            <InputField
              label="Phone"
              value={(formData.owner_phone as string) || ""}
              onChange={(v) => handleChange("owner_phone", v)}
              type="tel"
              placeholder="+1 (555) 123-4567"
            />
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1.5">
                Address
              </label>
              <textarea
                value={(formData.owner_address as string) || ""}
                onChange={(e) => handleChange("owner_address", e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent resize-none"
                placeholder="123 Main St, City, State 12345"
              />
            </div>
          </div>
        )}

        {/* Domain & URLs */}
        {activeSection === "domain" && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1.5">
                Subdomain
              </label>
              <div className="flex items-center">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={(formData.subdomain as string) || ""}
                    onChange={(e) =>
                      handleChange(
                        "subdomain",
                        e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                      )
                    }
                    className={`w-full px-4 py-2.5 pr-10 border rounded-l-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                      subdomainStatus.available === true
                        ? "border-green-400 focus:ring-green-500"
                        : subdomainStatus.available === false
                          ? "border-red-400 focus:ring-red-500"
                          : "border-gray-300 focus:ring-[#374795]"
                    }`}
                    placeholder="acme"
                  />
                  {((formData.subdomain as string) || "").length >= 3 &&
                    (formData.subdomain as string) !== originalSubdomain && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {subdomainStatus.checking ? (
                          <TbLoader className="w-4 h-4 animate-spin text-gray-400" />
                        ) : subdomainStatus.available === true ? (
                          <TbCheck className="w-4 h-4 text-green-500" />
                        ) : subdomainStatus.available === false ? (
                          <TbX className="w-4 h-4 text-red-500" />
                        ) : null}
                      </div>
                    )}
                </div>
                <span className="px-4 py-2.5 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-sm text-[#828282]">
                  .taskrsol.com
                </span>
              </div>
              {((formData.subdomain as string) || "").length >= 3 &&
                (formData.subdomain as string) !== originalSubdomain &&
                !subdomainStatus.checking && (
                  <p
                    className={`mt-1.5 text-xs ${
                      subdomainStatus.available === true
                        ? "text-green-600"
                        : subdomainStatus.available === false
                          ? "text-red-500"
                          : "text-[#828282]"
                    }`}
                  >
                    {subdomainStatus.available === true
                      ? `${formData.subdomain}.taskrsol.com is available`
                      : subdomainStatus.available === false
                        ? subdomainStatus.reason || "Not available"
                        : ""}
                  </p>
                )}
            </div>
            <InputField
              label="Custom Domain (optional)"
              value={(formData.custom_domain as string) || ""}
              onChange={(v) => handleChange("custom_domain", v)}
              placeholder="tasks.yourdomain.com"
            />
            <InputField
              label="Website URL"
              value={(formData.website_url as string) || ""}
              onChange={(v) => handleChange("website_url", v)}
              type="url"
              placeholder="https://www.yourdomain.com"
            />
            <InputField
              label="Redirect URL"
              value={(formData.redirect_url as string) || ""}
              onChange={(v) => handleChange("redirect_url", v)}
              type="url"
              placeholder="https://www.yourdomain.com"
            />
          </div>
        )}

        {/* Features & Limits */}
        {activeSection === "features" && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1.5">
                Plan
              </label>
              <select
                value={formData.plan_id || "custom"}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  if (selectedId === "custom") {
                    handleChange("plan_id", null);
                    return;
                  }
                  const plan = plans.find((p) => p.id === selectedId);
                  if (plan) {
                    setFormData((prev) => ({
                      ...prev,
                      plan_id: plan.id,
                      plan_tier: plan.slug,
                      max_users: plan.max_users,
                      max_tasks: plan.max_tasks,
                      feature_superset: plan.feature_superset,
                      feature_arcgis: plan.feature_arcgis,
                      feature_realtime: plan.feature_realtime,
                      feature_bulk_assignments: plan.feature_bulk_assignments,
                      feature_reporting: plan.feature_reporting,
                      feature_asset_management: plan.feature_asset_management,
                    }));
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
              >
                {plans
                  .filter((p) => p.is_active)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.price_monthly > 0 ? ` ($${p.price_monthly}/mo)` : ""}
                    </option>
                  ))}
                <option value="custom">Custom (manual override)</option>
              </select>
              {formData.plan_id && (
                <p className="text-xs text-[#828282] mt-1">
                  Limits and features are managed by the plan. Switch to
                  &quot;Custom&quot; to override manually.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1.5">
                  Max Users
                </label>
                <input
                  type="number"
                  value={formData.max_users || 0}
                  onChange={(e) =>
                    handleChange("max_users", parseInt(e.target.value) || 0)
                  }
                  disabled={!!formData.plan_id}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent disabled:bg-gray-100 disabled:text-[#828282]"
                />
                {formData.max_users === 0 && (
                  <p className="text-xs text-[#828282] mt-1">0 = unlimited</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1.5">
                  Max Tasks
                </label>
                <input
                  type="number"
                  value={formData.max_tasks || 0}
                  onChange={(e) =>
                    handleChange("max_tasks", parseInt(e.target.value) || 0)
                  }
                  disabled={!!formData.plan_id}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent disabled:bg-gray-100 disabled:text-[#828282]"
                />
                {formData.max_tasks === 0 && (
                  <p className="text-xs text-[#828282] mt-1">0 = unlimited</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-[#444]">Feature Flags</p>
              {FEATURE_FLAGS.map(({ key, label }) => (
                <div
                  key={key}
                  className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm text-[#444]">{label}</span>
                  <button
                    onClick={() =>
                      handleChange(key, !formData[key as keyof typeof formData])
                    }
                    disabled={!!formData.plan_id}
                    className="text-[#828282] hover:text-[#444] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formData[key as keyof typeof formData] ? (
                      <TbToggleRight className="w-7 h-7 text-green-500" />
                    ) : (
                      <TbToggleLeft className="w-7 h-7 text-gray-400" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Database */}
        {activeSection === "database" && tenantId && (
          <DatabaseSection tenantId={tenantId} />
        )}

        {/* Save */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={() => navigate("/platform/tenants")}
            className="px-5 py-2.5 text-sm font-medium text-[#444] bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={
              saving ||
              subdomainStatus.available === false ||
              subdomainStatus.checking
            }
            className="px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

const InputField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-[#444] mb-1.5">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
      placeholder={placeholder}
    />
  </div>
);

const ColorField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-[#444] mb-1.5">
      {label}
    </label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
      />
    </div>
  </div>
);

const DatabaseSection: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [connectionOk, setConnectionOk] = useState<boolean | null>(null);
  const [config, setConfig] = useState<TenantDbConfig | null>(null);
  const [dbForm, setDbForm] = useState({
    supabase_url: "",
    supabase_anon_key: "",
    supabase_service_role_key: "",
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const existing = await TenantService.getTenantDbConfig(tenantId);
        if (existing) {
          setConfig(existing);
          setDbForm({
            supabase_url: existing.supabase_url || "",
            supabase_anon_key: existing.supabase_anon_key || "",
            supabase_service_role_key: "",
          });
        }
      } catch {
        toast.error("Failed to load database config");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId]);

  const handleSaveConfig = async () => {
    if (
      !dbForm.supabase_url ||
      !dbForm.supabase_anon_key ||
      !dbForm.supabase_service_role_key
    ) {
      toast.error("All fields are required");
      return;
    }
    setSaving(true);
    try {
      const saved = await TenantService.saveTenantDbConfig(tenantId, dbForm);
      setConfig(saved);
      setConnectionOk(null);
      toast.success("Database config saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionOk(null);
    try {
      const result = await TenantService.testTenantDbConnection(tenantId);
      setConnectionOk(result.success);
      if (result.success) {
        toast.success("Connection successful");
      } else {
        toast.error(result.error || "Connection failed");
      }
    } catch {
      setConnectionOk(false);
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const result = await TenantService.provisionTenantDb(tenantId);
      if (result.success) {
        toast.success("Database provisioned successfully");
        const updated = await TenantService.getTenantDbConfig(tenantId);
        setConfig(updated);
      } else {
        toast.error(result.error || "Provisioning failed");
      }
    } catch {
      toast.error("Provisioning failed");
    } finally {
      setProvisioning(false);
    }
  };

  const handleMigrate = async (direction: "to-dedicated" | "to-shared") => {
    const label =
      direction === "to-dedicated"
        ? "shared → dedicated"
        : "dedicated → shared";
    if (
      !window.confirm(
        `Are you sure you want to migrate data ${label}? Existing data in the destination will be overwritten where IDs match.`
      )
    ) {
      return;
    }

    setMigrating(true);
    try {
      const result =
        direction === "to-dedicated"
          ? await TenantService.migrateTenantToDedicatedDb(tenantId)
          : await TenantService.migrateTenantToSharedDb(tenantId);

      if (result.success) {
        toast.success("Migration completed successfully");
      } else {
        toast.error(result.error || "Migration failed");
      }

      // Reload config to get updated migration_status
      const updated = await TenantService.getTenantDbConfig(tenantId);
      setConfig(updated);
    } catch {
      toast.error("Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <TbLoader className="w-6 h-6 animate-spin text-[#374795]" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Status */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
        <TbDatabase className="w-5 h-5 text-[#374795]" />
        <div className="flex-1">
          <p className="text-sm font-medium text-[#444]">Dedicated Database</p>
          <p className="text-xs text-[#828282]">
            {config?.is_provisioned
              ? `Provisioned on ${new Date(config.provisioned_at!).toLocaleDateString()}`
              : config
                ? "Configured but not provisioned"
                : "No dedicated database configured"}
          </p>
        </div>
        {config?.is_provisioned ? (
          <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
            <TbCheck className="w-3.5 h-3.5" /> Active
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
            Not provisioned
          </span>
        )}
      </div>

      {/* Credentials form */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Supabase URL
          </label>
          <input
            type="text"
            value={dbForm.supabase_url}
            onChange={(e) =>
              setDbForm((prev) => ({ ...prev, supabase_url: e.target.value }))
            }
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            placeholder="https://xxxxx.supabase.co"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Supabase Anon Key
          </label>
          <input
            type="text"
            value={dbForm.supabase_anon_key}
            onChange={(e) =>
              setDbForm((prev) => ({
                ...prev,
                supabase_anon_key: e.target.value,
              }))
            }
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent font-mono"
            placeholder="eyJhbGciOiJI..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Supabase Service Role Key
          </label>
          <input
            type="password"
            value={dbForm.supabase_service_role_key}
            onChange={(e) =>
              setDbForm((prev) => ({
                ...prev,
                supabase_service_role_key: e.target.value,
              }))
            }
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent font-mono"
            placeholder={
              config ? "(saved — enter new value to update)" : "eyJhbGciOiJI..."
            }
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          className="px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] disabled:opacity-50 transition-colors cursor-pointer"
        >
          {saving ? "Saving..." : "Save Credentials"}
        </button>

        <button
          onClick={handleTestConnection}
          disabled={testing || !config}
          className="px-5 py-2.5 text-sm font-medium text-[#374795] bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {testing ? "Testing..." : "Test Connection"}
        </button>

        {connectionOk !== null && (
          <span
            className={`flex items-center gap-1 text-sm ${connectionOk ? "text-green-600" : "text-red-500"}`}
          >
            {connectionOk ? (
              <TbCheck className="w-4 h-4" />
            ) : (
              <TbX className="w-4 h-4" />
            )}
            {connectionOk ? "Connected" : "Failed"}
          </span>
        )}
      </div>

      {/* Provision button */}
      {config && !config.is_provisioned && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm text-[#828282] mb-3">
            Provision the dedicated database with the required schema, roles,
            and seed data.
          </p>
          <button
            onClick={handleProvision}
            disabled={provisioning || connectionOk !== true}
            className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {provisioning ? "Provisioning..." : "Provision Schema"}
          </button>
          {connectionOk !== true && (
            <p className="text-xs text-[#828282] mt-2">
              Test the connection before provisioning.
            </p>
          )}
        </div>
      )}

      {/* Data Migration */}
      {config?.is_provisioned && (
        <div className="border-t border-gray-200 pt-4 space-y-4">
          <p className="text-sm font-medium text-[#444]">Data Migration</p>

          {/* Migration status badge */}
          {config.migration_status === "completed" && (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <TbCheck className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">
                Last migration completed on{" "}
                {config.migrated_at
                  ? new Date(config.migrated_at).toLocaleString()
                  : "N/A"}
              </span>
            </div>
          )}

          {config.migration_status === "failed" && (
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <TbX className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700">Migration failed</span>
              </div>
              {config.migration_error && (
                <p className="text-xs text-red-600 mt-1">
                  {config.migration_error}
                </p>
              )}
            </div>
          )}

          {config.migration_status === "in_progress" && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <TbLoader className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-sm text-blue-700">
                Migration in progress...
              </span>
            </div>
          )}

          {/* Migration log summary */}
          {config.migration_log && config.migration_log.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-[#828282] mb-2">
                Migration Summary
              </p>
              <div className="grid grid-cols-2 gap-1">
                {config.migration_log.map((entry: TableMigrationResult) => (
                  <div
                    key={entry.table}
                    className="flex items-center justify-between text-xs py-1 px-2"
                  >
                    <span className="text-[#444] font-mono">{entry.table}</span>
                    <span
                      className={
                        entry.status === "success"
                          ? "text-green-600"
                          : entry.status === "skipped"
                            ? "text-gray-400"
                            : "text-red-500"
                      }
                    >
                      {entry.status === "success"
                        ? `${entry.rowCount} rows`
                        : entry.status === "skipped"
                          ? "skipped"
                          : "failed"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Migration buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleMigrate("to-dedicated")}
              disabled={migrating || config.migration_status === "in_progress"}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] disabled:opacity-50 transition-colors cursor-pointer"
            >
              {migrating ? (
                <TbLoader className="w-4 h-4 animate-spin" />
              ) : (
                <TbArrowRight className="w-4 h-4" />
              )}
              {migrating ? "Migrating..." : "Shared → Dedicated"}
            </button>

            <button
              onClick={() => handleMigrate("to-shared")}
              disabled={migrating || config.migration_status === "in_progress"}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-[#374795] bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {migrating ? (
                <TbLoader className="w-4 h-4 animate-spin" />
              ) : (
                <TbArrowLeft className="w-4 h-4" />
              )}
              {migrating ? "Migrating..." : "Dedicated → Shared"}
            </button>
          </div>

          <p className="text-xs text-[#828282]">
            Migrate tenant data between the shared database and the dedicated
            database. Existing records with matching IDs will be overwritten
            (upsert).
          </p>
        </div>
      )}
    </div>
  );
};
